import { useContext } from "react";

import {
  FileContext,
  type FileContextType,
  useFileStoreValue,
} from "./file-context";

/**
 * Attachment storage for a chat composer: the surrounding `FileProvider` when
 * there is one, a private store when there isn't.
 *
 * Both models are load-bearing, which is why this is an adapter rather than a
 * choice. Page-level surfaces (agents chat/session, lifecycle generate, the
 * campaign and report modals) mount a provider precisely so page chrome outside
 * the composer can participate — `PageFileDropZone` writes dropped files in,
 * and several modals read them back out to bridge into their own form state.
 * Standalone composers (Ad Studio landing, context hub styles) mount no
 * provider and just own their files.
 *
 * Before this hook each composer hard-coded one model at authoring time, so
 * capabilities attached to the wrong layer: drag-and-drop lived on the provider
 * for one composer and inside the component for the other, and adding it to one
 * did nothing for the other.
 */
export const useComposerFileStore = (): FileContextType => {
  const providedStore = useContext(FileContext);
  // Always instantiated so hook order stays stable; ignored when a provider
  // is present, which is the common case on page-level surfaces.
  const localStore = useFileStoreValue();

  return providedStore ?? localStore;
};
